/**
 * Icon box + image (acf.icon_box_image).
 * Left: existing box card (icon, header, body). Right: image.
 * Fields: icon, header, body, image, background_colour, figure, button.
 */

import Image from "next/image";
import Link from "next/link";
import { OpsiHtmlContent } from "../utils/OpsiHtmlContent";
import {
  getFigureText,
  getMediaUrl,
  hasHtmlContent,
  OPSI_PART_SPACING,
  OPSI_EXTERNAL_LINK_PROPS,
  resolveBackgroundColour,
  resolveOpsiUrl,
} from "../utils/opsiHelpers";

const OPSI_ACCENT = "#993189";
const OPSI_CARD_BG_FALLBACK = "#f7f7f7";
const OPSI_COLUMN_GAP = 30;

const OpsiBoxCardButton = ({ href, text }) => {
  if (!href) {
    return null;
  }

  return (
    <Link
      href={href}
      {...OPSI_EXTERNAL_LINK_PROPS}
      className="mt-auto inline-grid h-[44px] min-w-[100px] place-items-center self-start rounded-full px-8 text-[1rem] font-semibold leading-none text-white no-underline transition hover:opacity-90"
      style={{ backgroundColor: OPSI_ACCENT }}
    >
      <span>{text}</span>
    </Link>
  );
};

export const OpsiIconBoxImage = ({
  data,
  className = `opsi-icon-box-image ${OPSI_PART_SPACING}`,
}) => {
  const item = data || {};
  const image = item.image;
  const imageUrl = getMediaUrl(image);
  const iconUrl = getMediaUrl(item?.icon);
  const figureText = getFigureText(item?.figure);
  const buttonLink = resolveOpsiUrl(item?.button?.button_link);
  const buttonText = item?.button?.button_text || "View";
  const cardBackground = resolveBackgroundColour(item, OPSI_CARD_BG_FALLBACK);
  const hasCardContent =
    Boolean(iconUrl) ||
    Boolean(figureText) ||
    hasHtmlContent(item?.header) ||
    hasHtmlContent(item?.body) ||
    Boolean(buttonLink);

  if (!hasCardContent && !imageUrl) {
    return null;
  }

  return (
    <section className={className} data-opsi-part="icon-box-image">
      <div
        className="grid w-full grid-cols-1 items-stretch md:grid-cols-2"
        style={{ gap: `${OPSI_COLUMN_GAP}px` }}
      >
        {hasCardContent ? (
          <article
            className="opsi-icon-box-image__card flex h-full w-full flex-col rounded-[20px] !p-[2rem] max-[850px]:!p-[1.5rem]"
            style={{ backgroundColor: cardBackground }}
          >
            {iconUrl ? (
              <Image
                src={iconUrl}
                alt={item?.icon?.alt || ""}
                width={item?.icon?.width || 80}
                height={item?.icon?.height || 80}
                className="mb-4 h-[48px] w-[48px] flex-none object-contain md:h-[80px] md:w-[80px]"
              />
            ) : null}

            {figureText ? (
              <p
                className="!m-0 !text-[4rem] !font-bold leading-none md:!text-[4.5rem]"
                style={{ color: OPSI_ACCENT }}
              >
                {figureText}
              </p>
            ) : null}

            {hasHtmlContent(item?.header) ? (
              <OpsiHtmlContent
                html={item.header}
                as="h3"
                className="opsi-icon-box-image__header !m-0 !mt-4 !text-[1.3rem] !font-[500] !text-[#000] leading-tight [&_*]:!text-[#000]"
              />
            ) : null}

            {hasHtmlContent(item?.body) ? (
              <OpsiHtmlContent
                html={item.body}
                className="opsi-icon-box-image__body !mt-3 min-h-0 flex-1 !py-5 !text-[1rem] !font-medium !text-[#000] leading-snug [&_*]:!text-[#000] [&_li]:!mb-2 [&_p]:!m-0 [&_ul]:!mb-0 [&_ul]:!list-disc max-[420px]:!p-0"
              />
            ) : null}

            {buttonLink ? (
              <OpsiBoxCardButton href={buttonLink} text={buttonText} />
            ) : null}
          </article>
        ) : null}

        {imageUrl ? (
          <div className="opsi-icon-box-image__media relative min-h-[220px] overflow-hidden rounded-[20px] md:aspect-auto md:h-full md:min-h-full">
            <Image
              src={imageUrl}
              alt={image?.alt || ""}
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover"
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};
